import { FC } from "react";

import { sleep } from "utils/helper";

import { SavedFilterModel } from "models";
import { EntityKind } from "models/entityKind";

import {
  AutomationHandler,
  useRegisterHandlerWithBroker
} from "components/automation/contexts/AutomationBrokerContext";
import { AutomationCommand } from "components/automation/types/automation";
import { LoadSavedFilterPayload } from "components/automation/types/payloads";
import { CommandResult } from "components/automation/types/results";
import { Topic, Topics } from "components/automation/types/topics";
import { useChangeProject } from "components/project/projects/hooks/useChangeProject";

export type SavedFilterAutomationProps = {
  entityKind: EntityKind;
  selectedFilter: SavedFilterModel;
  onSelectedFilterChanged: (filter: SavedFilterModel) => void;
  savedFilters: SavedFilterModel[];
};

export const SavedFilterAutomation: FC<SavedFilterAutomationProps> = ({
  entityKind,
  savedFilters,
  selectedFilter,
  onSelectedFilterChanged
}) => {
  const { update: changeProject } = useChangeProject();

  const handler: AutomationHandler = {
    id: `saved-filter-loader-${entityKind}`,
    handles: function (topic: Topic): boolean {
      return (
        topic === Topics.LoadSavedFilter ||
        topic == Topics.GetProjectIdAndSavedFilterInfo ||
        topic == Topics.LoadProject
      );
    },
    executeCommand: async function (command: AutomationCommand): Promise<CommandResult> {
      if (
        command.topic !== Topics.LoadSavedFilter &&
        command.topic !== Topics.GetProjectIdAndSavedFilterInfo &&
        command.topic !== Topics.LoadProject
      ) {
        return;
      }

      if (command.topic === Topics.GetProjectIdAndSavedFilterInfo) {
        return Promise.resolve({
          status: "ok",
          payload: {
            projectId: selectedFilter.projectID,
            filterId: selectedFilter.id,
            entityKind,
            filterName: selectedFilter.name
          }
        });
      }
      if (command.topic === Topics.LoadProject) {
        const payload = command.payload as LoadSavedFilterPayload;
        if (!payload) {
          return;
        }
        const projectId = payload.projectId;
        changeProject(projectId);
        await sleep(1000);
        return Promise.resolve({
          status: "ok"
        });
      }
      const payload = command.payload as LoadSavedFilterPayload;
      if (!payload) {
        return;
      }
      const name = payload.filterName;
      const match = savedFilters.find((f) => f.name === name);

      if (match) {
        onSelectedFilterChanged && onSelectedFilterChanged(match);
        //wait for selection to update
        await sleep(2000);
      }

      return Promise.resolve({
        status: "ok"
      });
    }
  };

  useRegisterHandlerWithBroker(handler);

  return <></>;
};
